import { useState, useRef, type FormEvent } from "react";
import {
  LINEWEIGHT_PRESET_VALUES,
  normalizeLineweightValue,
} from "./constants";
import type { LayerDefinition } from "@dxf-ai-editor/drawing-model";
import { useT, type MessageKey } from "../i18n";
import { useEditorStore } from "../state/editorStore";

// Phase 1.k (П35Р React #185 fix) — stable empty array sentinel. The
// fallback `?? []` inside the Zustand selector below allocated a fresh
// array on every store update, defeating `useSyncExternalStore`'s
// snapshot bail-out. The dev-React message
// `forceStoreRerender → updateStoreInstance → commitHookEffectListMount`
// is exactly the symptom of tearing detection trying to recover from
// inconsistent snapshots; with 685k entities + many subscribers it
// stacked above React's 50-update depth budget → error #185.
const EMPTY_LAYERS: LayerDefinition[] = [];
import { aciToHex } from "../scene/aciColorMap";
import { isHexColorString } from "../color/rgbCodec";
import { AciColorPicker } from "./AciColorPicker";
import {
  STANDARD_LINETYPES,
  findStandardLinetype,
  isLinetypeProperty,
  entityAllowsLinetype,
} from "../linetypes";

const attachmentLabels = ["TL", "TC", "TR", "ML", "MC", "MR", "BL", "BC", "BR"];
const attachmentCodes = ["7", "8", "9", "4", "5", "6", "1", "2", "3"];

// Attachment display names are now i18n keys — resolved at render time
// via t(). The English fallback values stay here for code-level
// references (e.g. attachmentNameToCode reverse lookup).
const attachmentDisplayNameKeys: Record<string, MessageKey> = {
  "7": "property.attachment.top_left",
  "8": "property.attachment.top_center",
  "9": "property.attachment.top_right",
  "4": "property.attachment.middle_left",
  "5": "property.attachment.middle_center",
  "6": "property.attachment.middle_right",
  "1": "property.attachment.bottom_left",
  "2": "property.attachment.bottom_center",
  "3": "property.attachment.bottom_right",
};
// Keep English names for the reverse lookup (propertyDraft stores
// the English name, not the translated one).
const attachmentDisplayNames: Record<string, string> = {
  "7": "Top left",
  "8": "Top center",
  "9": "Top right",
  "4": "Middle left",
  "5": "Middle center",
  "6": "Middle right",
  "1": "Bottom left",
  "2": "Bottom center",
  "3": "Bottom right",
};

/**
 * Translate a property label from its English key form (used in
 * propertyDraft and previewBuilders) into the current UI language.
 * Falls back to the original English label if no translation key
 * exists — so new properties work immediately without i18n changes.
 */
function translatePropertyLabel(
  label: string,
  t: (key: MessageKey) => string,
): string {
  const key = ("property." +
    label.toLowerCase().replace(/ /g, "_")) as MessageKey;
  const translated = t(key);
  // t() returns the key itself if not found — detect that case
  return translated !== key ? translated : label;
}
const attachmentNameToCode: Record<string, string> = Object.fromEntries(
  Object.entries(attachmentDisplayNames).map(([code, name]) => [name, code]),
);

export function resolveAttachmentCode(value: string): string {
  if (attachmentCodes.includes(value)) return value;
  return attachmentNameToCode[value] ?? "7";
}

export function AttachmentGrid({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const t = useT();
  const activeCode = resolveAttachmentCode(value);
  return (
    <div className="attachment-grid">
      {attachmentLabels.map((label, i) => {
        const code = attachmentCodes[i];
        const nameKey = attachmentDisplayNameKeys[code];
        return (
          <button
            key={label}
            type="button"
            className={`attachment-cell ${activeCode === code ? "is-active" : ""}`}
            onClick={() => onChange(attachmentDisplayNames[code])}
            title={nameKey ? t(nameKey) : label}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function isStyleProperty(label: string): boolean {
  return ["Text style", "Dimension style", "Multileader style"].includes(label);
}

export function isAnnotativeProperty(label: string): boolean {
  return label === "Annotative";
}

export function isLineweightProperty(label: string): boolean {
  return label === "Lineweight";
}

export function isColorProperty(label: string): boolean {
  return label === "Color";
}

export function isAttachmentProperty(
  label: string,
  entityType: string | null,
): boolean {
  return label === "Attachment" && entityType === "MText";
}

/**
 * AutoCAD-parity viewport scale list. Matches the Properties palette
 * dropdown shown by AutoCAD when a viewport is selected. The order
 * mirrors the Scale List (SCALELISTEDIT) defaults so user expectations
 * carry across tools.
 */
export const STANDARD_VIEWPORT_SCALES: readonly string[] = [
  "1:1",
  "1:2",
  "1:5",
  "1:10",
  "1:20",
  "1:25",
  "1:50",
  "1:100",
  "1:200",
  "1:500",
  "1:1000",
  "2:1",
  "5:1",
  "10:1",
  "20:1",
  "50:1",
  "100:1",
];

/** Marker value appended to the dropdown that reveals the free-form input. */
export const VIEWPORT_SCALE_CUSTOM_VALUE = "Custom";

export function isViewportScaleProperty(
  label: string,
  entityType: string | null,
): boolean {
  return label === "Scale" && entityType === "Viewport";
}

/**
 * Parse a scale string of the form "A:B" (e.g. "1:100", "2:1") into the
 * numeric ratio A/B. Returns null for unrecognised strings so callers
 * can fall back to free-form handling (Custom mode).
 */
export function parseViewportScaleRatio(value: string): number | null {
  // Split on ":" — more robust than a regex with nested optional groups
  // (and avoids tripping the security linter's ReDoS heuristic).
  const trimmed = value.trim();
  const colonIndex = trimmed.indexOf(":");
  if (colonIndex <= 0 || colonIndex >= trimmed.length - 1) return null;
  const leftPart = trimmed.slice(0, colonIndex).trim();
  const rightPart = trimmed.slice(colonIndex + 1).trim();
  if (leftPart.length === 0 || rightPart.length === 0) return null;
  const numerator = Number(leftPart);
  const denominator = Number(rightPart);
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator)) return null;
  if (denominator === 0) return null;
  return numerator / denominator;
}

/**
 * Per-parameter kind descriptor for dynamic block INSERT properties
 * (DYN-1.10). The host (App.tsx) looks up the resolved BlockDefinition
 * for the selected INSERT and supplies `kind` + optional metadata so
 * the PropertyGrid can render the right control (numeric input for
 * Linear, Yes/No toggle for Flip, dropdown for Visibility).
 */
export type DynamicPropertyDescriptor =
  | { kind: "linear"; valueSet?: { mode: string; listValues?: number[] } }
  | { kind: "flip" }
  | {
      kind: "visibility";
      states: Array<{ id: string; name: string }>;
    }
  | { kind: "basepoint" }
  // DYN-2 Tier 2
  | {
      kind: "point-x" | "point-y";
      valueSet?: { mode: string; listValues?: number[] };
    }
  | {
      kind: "polar-distance" | "polar-angle";
      valueSet?: { mode: string; listValues?: number[] };
    }
  | {
      kind: "xy-x" | "xy-y";
      valueSet?: { mode: string; listValues?: number[] };
    }
  | {
      kind: "rotation";
      valueSet?: { mode: string; listValues?: number[] };
    }
  | {
      kind: "lookup";
      rows: Array<{ id: string; label: string }>;
      reverseLookup: boolean;
    }
  // DYN-2 Batch C — dimensional constraint params (single numeric field)
  | {
      kind:
        | "constraint-horizontal"
        | "constraint-vertical"
        | "constraint-angular";
      valueSet?: { mode: string; listValues?: number[] };
    }
  // DYN-3 Batch E — Tier 3 Alignment + dimensional constraint params
  | {
      kind:
        | "alignment"
        | "constraint-aligned"
        | "constraint-radial"
        | "constraint-diameter";
      valueSet?: { mode: string; listValues?: number[] };
    };

export type PropertyGridProps = {
  propertiesView: {
    rows: Array<{
      label: string;
      value: string;
      /** S16-ARRAYRECT-READONLY-ALIGN (W2.1) — true renders this cell as a
       *  non-interactive display instead of an editable control (an edit
       *  here would silently no-op at the write boundary — AutoCAD itself
       *  greys these exact Properties-palette cells). Absent/`undefined`
       *  behaves as `false` (every pre-W2.1 row). */
      readOnly?: boolean;
    }>;
  };
  propertyDraft: Record<string, string>;
  setPropertyDraft: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  contextEntityType: string | null;
  getStyleOptions: (label: string) => string[];
  submitProperties: (event: FormEvent<HTMLFormElement>) => void;
  /** Auto-apply a single property change (AutoCAD parity: blur / Enter / dropdown change). */
  onAutoApply?: () => void;
  onOpenLayerManager?: () => void;
  /**
   * DYN-1.10 — return the descriptor for a dynamic-block property row
   * (Param: <name> or Visibility) or null if the row is not dynamic.
   * Called for every row whose label starts with "Param: " or equals
   * "Visibility" on a Block entity. Host implementation reads from
   * `geometryDocument.blocks[entityBlockName].dynamicMetadata`.
   */
  getDynamicPropertyDescriptor?: (
    rowLabel: string,
  ) => DynamicPropertyDescriptor | null;
};

export function PropertyGridRows({
  propertiesView,
  propertyDraft,
  setPropertyDraft,
  contextEntityType,
  getStyleOptions,
  onAutoApply,
  onOpenLayerManager,
  getDynamicPropertyDescriptor,
}: Omit<PropertyGridProps, "submitProperties">) {
  const t = useT();
  const layers = useEditorStore(
    (store) => store.geometryDocument.styleTables?.layers ?? EMPTY_LAYERS,
  );

  // AutoCAD parity: schedule auto-apply after React processes the
  // setPropertyDraft state update so the parent ref captures the
  // latest draft values.
  const scheduleAutoApply = () => {
    if (onAutoApply) setTimeout(onAutoApply, 0);
  };

  // DYN-1.10 helper — classify each row as either static (go through
  // existing rendering) or dynamic (render custom control per kind).
  // Returns null for rows that are not dynamic so the default input
  // path is used.
  const dynamicDescriptor = (
    label: string,
  ): DynamicPropertyDescriptor | null =>
    getDynamicPropertyDescriptor ? getDynamicPropertyDescriptor(label) : null;

  return (
    <>
      {propertiesView.rows.map((row) => {
        const dyn = dynamicDescriptor(row.label);
        if (dyn) {
          // ── Dynamic property ──────────────────────────
          if (dyn.kind === "visibility") {
            return (
              <label
                key={row.label}
                className="property-editor-row property-editor-row-dynamic"
                data-testid={`property-dynamic-visibility-${row.label}`}
              >
                <span>{t("property.custom.visibility")}</span>
                <select
                  className="property-input"
                  data-testid="property-visibility-select"
                  value={propertyDraft[row.label] ?? row.value ?? ""}
                  onChange={(event) => {
                    setPropertyDraft((currentDraft) => ({
                      ...currentDraft,
                      [row.label]: event.target.value,
                    }));
                    scheduleAutoApply();
                  }}
                >
                  {dyn.states.map((state) => (
                    <option key={state.id} value={state.name}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </label>
            );
          }
          if (dyn.kind === "flip") {
            const paramName = row.label.startsWith("Param: ")
              ? row.label.slice("Param: ".length)
              : row.label;
            const currentValue = propertyDraft[row.label] ?? row.value ?? "No";
            const isYes =
              currentValue === "Yes" ||
              currentValue === "true" ||
              currentValue === "1";
            return (
              <label
                key={row.label}
                className="property-editor-row property-editor-row-dynamic"
                data-testid={`property-dynamic-flip-${paramName}`}
              >
                <span>
                  {t("property.custom.param_prefix")}: {paramName}
                </span>
                <select
                  className="property-input"
                  data-testid={`property-flip-select-${paramName}`}
                  value={isYes ? "Yes" : "No"}
                  onChange={(event) => {
                    setPropertyDraft((currentDraft) => ({
                      ...currentDraft,
                      [row.label]: event.target.value,
                    }));
                    scheduleAutoApply();
                  }}
                >
                  <option value="Yes">{t("annotative.yes")}</option>
                  <option value="No">{t("annotative.no")}</option>
                </select>
              </label>
            );
          }
          if (dyn.kind === "linear") {
            const paramName = row.label.startsWith("Param: ")
              ? row.label.slice("Param: ".length)
              : row.label;
            const listValues =
              dyn.valueSet?.mode === "list" ? dyn.valueSet.listValues : null;
            const datalistId = `dyn-linear-datalist-${paramName}`;
            return (
              <label
                key={row.label}
                className="property-editor-row property-editor-row-dynamic"
                data-testid={`property-dynamic-linear-${paramName}`}
              >
                <span>
                  {t("property.custom.param_prefix")}: {paramName}
                </span>
                <input
                  className="property-input"
                  type="number"
                  inputMode="decimal"
                  value={propertyDraft[row.label] ?? row.value ?? ""}
                  list={listValues ? datalistId : undefined}
                  onChange={(event) =>
                    setPropertyDraft((currentDraft) => ({
                      ...currentDraft,
                      [row.label]: event.target.value,
                    }))
                  }
                  onBlur={scheduleAutoApply}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      scheduleAutoApply();
                    }
                  }}
                />
                {listValues ? (
                  <datalist id={datalistId}>
                    {listValues.map((v) => (
                      <option key={v} value={String(v)} />
                    ))}
                  </datalist>
                ) : null}
              </label>
            );
          }
          // DYN-2 numeric Tier 2 kinds — same numeric input UI as
          // `linear`; the property-grid key suffix (.x/.y/.distance/
          // .angle / bare) determines which parameter-level property
          // the write lands on. Value Set list/increment applies
          // identically for all numeric kinds.
          if (
            dyn.kind === "point-x" ||
            dyn.kind === "point-y" ||
            dyn.kind === "polar-distance" ||
            dyn.kind === "polar-angle" ||
            dyn.kind === "xy-x" ||
            dyn.kind === "xy-y" ||
            dyn.kind === "rotation" ||
            dyn.kind === "constraint-horizontal" ||
            dyn.kind === "constraint-vertical" ||
            dyn.kind === "constraint-angular" ||
            dyn.kind === "alignment" ||
            dyn.kind === "constraint-aligned" ||
            dyn.kind === "constraint-radial" ||
            dyn.kind === "constraint-diameter"
          ) {
            const listValues =
              dyn.valueSet?.mode === "list" ? dyn.valueSet.listValues : null;
            const datalistId = `dyn-${dyn.kind}-datalist-${row.label}`;
            return (
              <label
                key={row.label}
                className="property-editor-row property-editor-row-dynamic"
                data-testid={`property-dynamic-${dyn.kind}-${row.label}`}
              >
                <span>{row.label}</span>
                <input
                  className="property-input"
                  type="number"
                  inputMode="decimal"
                  value={propertyDraft[row.label] ?? row.value ?? ""}
                  list={listValues ? datalistId : undefined}
                  onChange={(event) =>
                    setPropertyDraft((currentDraft) => ({
                      ...currentDraft,
                      [row.label]: event.target.value,
                    }))
                  }
                  onBlur={scheduleAutoApply}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      scheduleAutoApply();
                    }
                  }}
                />
                {listValues ? (
                  <datalist id={datalistId}>
                    {listValues.map((v) => (
                      <option key={v} value={String(v)} />
                    ))}
                  </datalist>
                ) : null}
              </label>
            );
          }
          // DYN-2 lookup — dropdown when reverseLookup=true (user picks
          // an output row), read-only label otherwise (forward lookup
          // cascades from inputs; user can't pick the row here).
          if (dyn.kind === "lookup") {
            if (!dyn.reverseLookup) {
              return (
                <label
                  key={row.label}
                  className="property-editor-row property-editor-row-dynamic"
                  data-testid={`property-dynamic-lookup-${row.label}`}
                >
                  <span>{row.label}</span>
                  <input
                    className="property-input"
                    readOnly
                    value={row.value ?? ""}
                  />
                </label>
              );
            }
            return (
              <label
                key={row.label}
                className="property-editor-row property-editor-row-dynamic"
                data-testid={`property-dynamic-lookup-${row.label}`}
              >
                <span>{row.label}</span>
                <select
                  className="property-input"
                  data-testid={`property-lookup-select-${row.label}`}
                  value={propertyDraft[row.label] ?? row.value ?? ""}
                  onChange={(event) => {
                    setPropertyDraft((currentDraft) => ({
                      ...currentDraft,
                      [row.label]: event.target.value,
                    }));
                    scheduleAutoApply();
                  }}
                >
                  {dyn.rows.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
            );
          }
          // basepoint — read-only display
          return (
            <label
              key={row.label}
              className="property-editor-row property-editor-row-dynamic"
              data-testid={`property-dynamic-basepoint-${row.label}`}
            >
              <span>{row.label}</span>
              <input
                className="property-input"
                readOnly
                value={row.value ?? ""}
              />
            </label>
          );
        }
        // ── Static property (existing behaviour) ─────────
        return (
          <label key={row.label} className="property-editor-row">
            {row.label === "Layer" ? (
              <button
                type="button"
                className="property-label-link"
                data-testid="property-layer-label-link"
                onClick={(e) => {
                  e.preventDefault();
                  onOpenLayerManager?.();
                }}
              >
                {translatePropertyLabel(row.label, t)}
              </button>
            ) : (
              <span>{translatePropertyLabel(row.label, t)}</span>
            )}
            {row.readOnly ? (
              // S16-ARRAYRECT-READONLY-ALIGN (W2.1) — a non-interactive
              // display, checked BEFORE every special-widget branch below so
              // a read-only cell is never accidentally rendered as an
              // editable select/input regardless of its label. Matches the
              // existing "linetype-readonly" span pattern (no input element
              // at all — AutoCAD greys these Properties-palette cells, it
              // does not show a disabled-but-present control).
              <span
                className="property-input property-input-readonly"
                data-testid={`property-readonly-${row.label}`}
              >
                {propertyDraft[row.label] ?? row.value ?? ""}
              </span>
            ) : row.label === "Layer" ? (
              <select
                className="property-input"
                data-testid="property-layer-select"
                value={propertyDraft[row.label] ?? row.value ?? "0"}
                onChange={(event) => {
                  setPropertyDraft((currentDraft) => ({
                    ...currentDraft,
                    [row.label]: event.target.value,
                  }));
                  scheduleAutoApply();
                }}
              >
                {layers.map((layer) => (
                  <option key={layer.name} value={layer.name}>
                    {layer.name}
                  </option>
                ))}
              </select>
            ) : isAttachmentProperty(row.label, contextEntityType) ? (
              <AttachmentGrid
                value={propertyDraft[row.label] ?? ""}
                onChange={(v) => {
                  setPropertyDraft((currentDraft) => ({
                    ...currentDraft,
                    [row.label]: v,
                  }));
                  scheduleAutoApply();
                }}
              />
            ) : isStyleProperty(row.label) ? (
              <select
                className="property-input"
                value={propertyDraft[row.label] ?? ""}
                onChange={(event) => {
                  setPropertyDraft((currentDraft) => ({
                    ...currentDraft,
                    [row.label]: event.target.value,
                  }));
                  scheduleAutoApply();
                }}
              >
                {getStyleOptions(row.label).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            ) : isAnnotativeProperty(row.label) ? (
              <select
                className="property-input"
                data-testid="property-annotative-select"
                value={
                  (propertyDraft[row.label] ?? row.value) === "Yes"
                    ? "Yes"
                    : "No"
                }
                onChange={(event) => {
                  setPropertyDraft((currentDraft) => ({
                    ...currentDraft,
                    [row.label]: event.target.value,
                  }));
                  scheduleAutoApply();
                }}
              >
                <option value="Yes">{t("annotative.yes")}</option>
                <option value="No">{t("annotative.no")}</option>
              </select>
            ) : isLineweightProperty(row.label) ? (
              (() => {
                const rawValue =
                  propertyDraft[row.label] ?? row.value ?? "ByLayer";
                const normalized = normalizeLineweightValue(rawValue);
                const isPreset = LINEWEIGHT_PRESET_VALUES.includes(normalized);
                const selectValue = isPreset ? normalized : rawValue;
                return (
                  <select
                    className="property-input"
                    data-testid="property-lineweight-select"
                    value={selectValue}
                    onChange={(event) => {
                      setPropertyDraft((currentDraft) => ({
                        ...currentDraft,
                        [row.label]: event.target.value,
                      }));
                      scheduleAutoApply();
                    }}
                  >
                    {LINEWEIGHT_PRESET_VALUES.map((value) => (
                      <option key={value} value={value}>
                        {value === "ByLayer"
                          ? t("lineweight.bylayer")
                          : value === "ByBlock"
                            ? t("lineweight.byblock")
                            : value === "Default"
                              ? t("lineweight.default")
                              : t("lineweight.mm", { value })}
                      </option>
                    ))}
                    {!isPreset ? (
                      <option key={rawValue} value={rawValue}>
                        {rawValue}
                      </option>
                    ) : null}
                  </select>
                );
              })()
            ) : isLinetypeProperty(row.label) ? (
              entityAllowsLinetype(contextEntityType) ? (
                (() => {
                  const rawValue =
                    propertyDraft[row.label] ?? row.value ?? "ByLayer";
                  // AutoCAD parity: if the entity carries a linetype name
                  // we've never seen (e.g. a custom one shipped with an
                  // imported DXF file), still preserve it as a selectable
                  // option so the round-trip is non-destructive.
                  const isKnown = findStandardLinetype(rawValue) !== undefined;
                  return (
                    <select
                      className="property-input"
                      data-testid="property-linetype-select"
                      value={rawValue}
                      onChange={(event) => {
                        setPropertyDraft((currentDraft) => ({
                          ...currentDraft,
                          [row.label]: event.target.value,
                        }));
                        scheduleAutoApply();
                      }}
                    >
                      {STANDARD_LINETYPES.map((lt) => (
                        <option key={lt.name} value={lt.name}>
                          {lt.name}
                        </option>
                      ))}
                      {!isKnown ? (
                        <option key={rawValue} value={rawValue}>
                          {rawValue}
                        </option>
                      ) : null}
                    </select>
                  );
                })()
              ) : (
                <span
                  className="property-input"
                  data-testid="property-linetype-readonly"
                >
                  {propertyDraft[row.label] ?? row.value ?? "ByLayer"}
                </span>
              )
            ) : isColorProperty(row.label) ? (
              <ColorPropertyCell
                value={propertyDraft[row.label] ?? row.value ?? ""}
                onChange={(v) => {
                  setPropertyDraft((currentDraft) => ({
                    ...currentDraft,
                    [row.label]: v,
                  }));
                  scheduleAutoApply();
                }}
              />
            ) : row.label === "Justify" ? (
              <select
                className="property-input"
                value={propertyDraft[row.label] ?? row.value}
                onChange={(event) => {
                  setPropertyDraft((currentDraft) => ({
                    ...currentDraft,
                    [row.label]: event.target.value,
                  }));
                  scheduleAutoApply();
                }}
              >
                <option value="Left">{t("justify.left")}</option>
                <option value="Center">{t("justify.center")}</option>
                <option value="Right">{t("justify.right")}</option>
                <option value="Aligned">{t("justify.aligned")}</option>
                <option value="Middle">{t("justify.middle")}</option>
                <option value="Fit">{t("justify.fit")}</option>
                <option value="TL">{t("justify.tl")}</option>
                <option value="TC">{t("justify.tc")}</option>
                <option value="TR">{t("justify.tr")}</option>
                <option value="ML">{t("justify.ml")}</option>
                <option value="MC">{t("justify.mc")}</option>
                <option value="MR">{t("justify.mr")}</option>
                <option value="BL">{t("justify.bl")}</option>
                <option value="BC">{t("justify.bc")}</option>
                <option value="BR">{t("justify.br")}</option>
              </select>
            ) : isViewportScaleProperty(row.label, contextEntityType) ? (
              (() => {
                // AutoCAD parity: Viewport Scale is a curated dropdown of
                // standard scales (1:1, 1:100, 2:1, ...) with a trailing
                // "Custom" option that reveals a free-form input for
                // non-standard ratios. Picking a standard scale also
                // recomputes View height from the paper Height atomically
                // so the model extent filling the viewport matches the
                // new scale without the user doing manual math.
                const currentRaw =
                  propertyDraft[row.label] ?? row.value ?? "";
                const isStandard =
                  STANDARD_VIEWPORT_SCALES.includes(currentRaw);
                const draftSelection = propertyDraft["__scaleMode"];
                // Show Custom when the draft explicitly asked for it or
                // when the value isn't in the standard list.
                const isCustom =
                  draftSelection === VIEWPORT_SCALE_CUSTOM_VALUE ||
                  (!isStandard && currentRaw.length > 0);
                const selectValue = isCustom
                  ? VIEWPORT_SCALE_CUSTOM_VALUE
                  : isStandard
                    ? currentRaw
                    : STANDARD_VIEWPORT_SCALES[0];
                return (
                  <div className="property-input-group">
                    <select
                      className="property-input"
                      data-testid="property-viewport-scale-select"
                      value={selectValue}
                      onChange={(event) => {
                        const picked = event.target.value;
                        if (picked === VIEWPORT_SCALE_CUSTOM_VALUE) {
                          setPropertyDraft((currentDraft) => ({
                            ...currentDraft,
                            __scaleMode: VIEWPORT_SCALE_CUSTOM_VALUE,
                          }));
                          // Do not auto-apply — wait for the user to type
                          // a custom value into the revealed input.
                          return;
                        }
                        // Compute view height atomically from paper height.
                        // View height = Height / ratio. For example, a
                        // 297mm-tall viewport at 1:100 shows 29700mm of
                        // model (297 / (1/100) = 297 * 100 = 29700).
                        const ratio = parseViewportScaleRatio(picked);
                        const heightRow = propertiesView.rows.find(
                          (r) => r.label === "Height",
                        );
                        const heightDraft = propertyDraft["Height"];
                        const currentHeight = Number(
                          heightDraft ?? heightRow?.value ?? "",
                        );
                        setPropertyDraft((currentDraft) => {
                          const next = { ...currentDraft };
                          next[row.label] = picked;
                          delete next["__scaleMode"];
                          if (
                            ratio !== null &&
                            ratio !== 0 &&
                            Number.isFinite(currentHeight) &&
                            currentHeight > 0
                          ) {
                            // AutoCAD parity: viewCenter stays; only
                            // viewHeight is recomputed from the new scale.
                            next["View height"] = String(currentHeight / ratio);
                          }
                          return next;
                        });
                        scheduleAutoApply();
                      }}
                    >
                      {STANDARD_VIEWPORT_SCALES.map((scale) => (
                        <option key={scale} value={scale}>
                          {scale}
                        </option>
                      ))}
                      <option value={VIEWPORT_SCALE_CUSTOM_VALUE}>
                        {t("viewport.scale.custom")}
                      </option>
                    </select>
                    {isCustom ? (
                      <input
                        className="property-input"
                        data-testid="property-viewport-scale-custom"
                        value={propertyDraft[row.label] ?? row.value ?? ""}
                        onChange={(event) =>
                          setPropertyDraft((currentDraft) => ({
                            ...currentDraft,
                            [row.label]: event.target.value,
                          }))
                        }
                        onBlur={scheduleAutoApply}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            scheduleAutoApply();
                          }
                        }}
                      />
                    ) : null}
                  </div>
                );
              })()
            ) : (
              <input
                className="property-input"
                value={propertyDraft[row.label] ?? ""}
                onChange={(event) =>
                  setPropertyDraft((currentDraft) => ({
                    ...currentDraft,
                    [row.label]: event.target.value,
                  }))
                }
                onBlur={scheduleAutoApply}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    scheduleAutoApply();
                  }
                }}
              />
            )}
          </label>
        );
      })}
    </>
  );
}

/**
 * Inline color property cell: shows a colored swatch + the current value.
 * Clicking opens an ACI color picker popup.
 */
function ColorPropertyCell({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // A true-colour value ("#rrggbb"/bare 6-hex) seeds the True Color tab and is
  // its own swatch colour; an ACI index seeds the Index tab + aciToHex swatch.
  const isTrueColor = isHexColorString(value);
  const aciNum = parseInt(value, 10);
  const swatchColor = isTrueColor
    ? value.startsWith("#")
      ? value
      : `#${value}`
    : Number.isFinite(aciNum)
      ? aciToHex(aciNum)
      : null;

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        type="button"
        className="property-color-trigger"
        onClick={() => setOpen((v) => !v)}
        data-testid="property-color-trigger"
      >
        {swatchColor ? (
          <span
            className="property-color-swatch"
            style={{ backgroundColor: swatchColor }}
          />
        ) : null}
        <span>{value}</span>
      </button>
      {open ? (
        <AciColorPicker
          currentAci={Number.isFinite(aciNum) ? aciNum : 7}
          anchorX={0}
          anchorY={triggerRef.current?.offsetHeight ?? 24}
          initialTab={isTrueColor ? "true" : "index"}
          initialHex={isTrueColor ? value : null}
          onSelect={(next) => {
            // Skip the apply when the selection is UNCHANGED from the current
            // display value: re-emitting the lossy "#rrggbb"/ACI projection of a
            // rich entity (rgb+aci, or bookColor whose grid display is the lossy
            // ACI fallback) would deserialize to a plain colour and drop the
            // 62/430 siblings S1/S2 preserve. A genuine change applies normally.
            if (next !== value) onChange(next);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}
